import { Button } from '@heidi/design-system/button';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { Link } from '@heidi/design-system/link';
import { TypographyCaption } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ArrowUpRight, Check, FileText, Loader2 } from 'lucide-react';
import NextLink from 'next/link';
import { useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import {
  formatVoiceToolLabel,
  formatVoiceUiActionLabel,
} from '../components/formatVoiceActivityLabel';
import {
  type TimelineEntry,
  useVoiceAssistantStore,
} from '../store/useVoiceAssistantStore';
import { InfoCardView } from './InfoCardView';
import { InlineDraftCard } from './InlineDraftCard';
import { VoiceAssistantMarkdown } from './VoiceAssistantMarkdown';
import { voiceWorkspaceMessages as messages } from './workspace.messages';
import type { InfoCard, WorkspaceThreadDraft } from './workspace.types';

type SpeakThreadEntry = Extract<
  TimelineEntry,
  { kind: 'transcript' } | { kind: 'tool' } | { kind: 'ui' }
>;

type ThreadItem =
  | { kind: 'speak'; entry: SpeakThreadEntry }
  | { kind: 'info'; card: InfoCard }
  | { kind: 'draft'; draft: WorkspaceThreadDraft };

const isSpeakThreadEntry = (entry: TimelineEntry): entry is SpeakThreadEntry =>
  entry.kind === 'transcript' || entry.kind === 'tool' || entry.kind === 'ui';

const mergeThreadItems = (
  speak: SpeakThreadEntry[],
  cards: InfoCard[],
  drafts: WorkspaceThreadDraft[]
): ThreadItem[] => {
  const items: ThreadItem[] = [
    ...speak.map((entry): ThreadItem => ({ kind: 'speak', entry })),
    ...cards.map((card): ThreadItem => ({ kind: 'info', card })),
    ...drafts.map((draft): ThreadItem => ({ kind: 'draft', draft })),
  ];
  items.sort((a, b) => {
    const ats =
      a.kind === 'speak' ? a.entry.ts : a.kind === 'info' ? a.card.ts : a.draft.ts;
    const bts =
      b.kind === 'speak' ? b.entry.ts : b.kind === 'info' ? b.card.ts : b.draft.ts;
    if (ats !== bts) return ats - bts;
    // Speak before cards/drafts on identical timestamps.
    const rank = (item: ThreadItem) =>
      item.kind === 'speak' ? 0 : item.kind === 'info' ? 1 : 2;
    return rank(a) - rank(b);
  });
  return items;
};

export interface VoiceTranscriptProps {
  className?: string;
  /** Info cards interleaved into the speak thread by timestamp. */
  infoCards?: InfoCard[];
  /** Draft version stack painted inline (not on the canvas). */
  drafts?: WorkspaceThreadDraft[];
  expandedDraftId?: string | null;
  onToggleDraft?: (id: string) => void;
  onDraftContentChange?: (id: string, content: string) => void;
  onDraftCreate?: (id: string) => void;
  onDraftDiscard?: (id: string) => void;
  followUpsEnabled?: boolean;
  onFollowUp?: (text: string) => void;
  stashedDraftTitle?: string;
  onBackToDraft?: () => void;
}

/**
 * Bottom-anchored Voice thread: Heidi left + muted (with mark), user right +
 * strong, tool/UI rows as compact status, info cards + drafts inline by time.
 * Grows upward; scrolls when tall.
 *
 * @see https://www.figma.com/design/5y5VGXMkw2812jPfNuNBS9/Alastair-s-Playground?node-id=312-41334
 */
export function VoiceTranscript({
  className,
  infoCards = [],
  drafts = [],
  expandedDraftId = null,
  onToggleDraft,
  onDraftContentChange,
  onDraftCreate,
  onDraftDiscard,
  followUpsEnabled = false,
  onFollowUp,
  stashedDraftTitle,
  onBackToDraft,
}: VoiceTranscriptProps) {
  const intl = useIntl();
  const timeline = useVoiceAssistantStore((s) => s.timeline);
  const minimizeWorkspace = useVoiceAssistantStore((s) => s.minimizeWorkspace);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const speakEntries = useMemo(
    () =>
      timeline.filter(isSpeakThreadEntry).filter((entry) => {
        if (entry.kind === 'transcript') return Boolean(entry.text?.trim());
        return true;
      }),
    [timeline]
  );

  const items = useMemo(
    () => mergeThreadItems(speakEntries, infoCards, drafts),
    [speakEntries, infoCards, drafts]
  );

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, [items.length, timeline, infoCards.length, drafts.length]);

  if (items.length === 0 && !stashedDraftTitle) return null;

  return (
    <div
      ref={scrollerRef}
      className={cn('min-h-0 overflow-y-auto', className)}
      data-testid="voice-transcript"
      aria-live="polite"
    >
      <div className="flex min-h-full flex-col justify-end p-4">
        <div className="mx-auto flex w-full max-w-xl flex-col gap-6">
          {stashedDraftTitle && onBackToDraft ? (
            <div className="flex items-center gap-2.5 rounded-xl border border-border/60 bg-surface px-3 py-2">
              <FileText className="size-4 shrink-0 text-text-tertiary" aria-hidden />
              <TypographyCaption className="min-w-0 flex-1 truncate text-text-secondary">
                {stashedDraftTitle}
              </TypographyCaption>
              <Button
                variant="ghost"
                size="sm"
                onClick={onBackToDraft}
                data-testid="workspace-back-to-draft"
              >
                {intl.formatMessage(messages.backToDraft)}
              </Button>
            </div>
          ) : null}

          {items.map((item) => {
            if (item.kind === 'info') {
              return (
                <div
                  key={`info:${item.card.id}`}
                  className="w-full"
                  data-testid="voice-thread-info-card"
                >
                  <InfoCardView
                    card={item.card}
                    followUpsEnabled={followUpsEnabled}
                    onFollowUp={onFollowUp ?? (() => undefined)}
                    presentation="inline"
                  />
                </div>
              );
            }

            if (item.kind === 'draft') {
              const { draft } = item;
              return (
                <div
                  key={`draft:${draft.id}`}
                  className="w-full"
                  data-testid="voice-thread-draft"
                >
                  <InlineDraftCard
                    draft={draft}
                    expanded={expandedDraftId === draft.id}
                    onToggle={() => onToggleDraft?.(draft.id)}
                    onContentChange={(content) =>
                      onDraftContentChange?.(draft.id, content)
                    }
                    onCreate={() => onDraftCreate?.(draft.id)}
                    onDiscard={() => onDraftDiscard?.(draft.id)}
                  />
                </div>
              );
            }

            const entry = item.entry;
            if (entry.kind === 'transcript') {
              const isUser = entry.role === 'user';
              return (
                <div
                  key={entry.id}
                  className={cn(
                    'flex w-full items-start gap-3',
                    isUser ? 'justify-end' : 'justify-start'
                  )}
                  data-role={entry.role}
                >
                  {!isUser ? (
                    <span className="mt-1 flex size-[17px] shrink-0 items-center justify-center text-text-secondary opacity-50">
                      <HeidiLogoMark className="size-[17px] text-text-secondary" />
                    </span>
                  ) : null}
                  {isUser ? (
                    <p className="max-w-[calc(100%-1.5rem)] text-pretty text-end text-xl font-medium leading-snug tracking-tight text-text-primary">
                      {entry.text}
                    </p>
                  ) : (
                    <VoiceAssistantMarkdown>{entry.text}</VoiceAssistantMarkdown>
                  )}
                </div>
              );
            }

            if (entry.kind === 'tool') {
              const running = entry.status === 'running';
              const label = formatVoiceToolLabel(intl, entry.name, entry.title);
              const projectHref = entry.projectId
                ? `/scribe/chat/projects/${encodeURIComponent(entry.projectId)}`
                : null;
              const statusIcon = running ? (
                <Loader2 className="size-3.5 shrink-0 animate-spin" aria-hidden />
              ) : (
                <Check className="size-3.5 shrink-0 text-text-tertiary" aria-hidden />
              );
              if (projectHref) {
                return (
                  <Link
                    key={entry.id}
                    asChild
                    size="xs"
                    className="mx-auto max-w-sm items-center gap-2 rounded-full bg-surface-accent/60 px-3 py-1.5"
                  >
                    <NextLink
                      href={projectHref}
                      prefetch={false}
                      onClick={(event) => {
                        // A new-tab click leaves this window where it was.
                        if (
                          event.button !== 0 ||
                          event.metaKey ||
                          event.ctrlKey ||
                          event.shiftKey ||
                          event.altKey
                        ) {
                          return;
                        }
                        minimizeWorkspace();
                      }}
                      aria-label={intl.formatMessage(messages.openWorkProject, {
                        label,
                      })}
                      data-testid="voice-thread-tool"
                      data-tool-status={entry.status}
                    >
                      {statusIcon}
                      <span className="truncate">{label}</span>
                      <ArrowUpRight aria-hidden className="shrink-0 rtl:-scale-x-100" />
                    </NextLink>
                  </Link>
                );
              }
              return (
                <div
                  key={entry.id}
                  className="mx-auto flex max-w-sm items-center gap-2 rounded-full bg-surface-accent/60 px-3 py-1.5 text-xs text-text-secondary"
                  data-testid="voice-thread-tool"
                  data-tool-status={entry.status}
                >
                  {statusIcon}
                  <span className="truncate">{label}</span>
                </div>
              );
            }

            return (
              <div
                key={entry.id}
                className="mx-auto max-w-sm text-center text-xs text-text-tertiary"
                data-testid="voice-thread-ui"
              >
                {formatVoiceUiActionLabel(intl, entry.action, entry.detail)}
              </div>
            );
          })}
          <div ref={bottomRef} aria-hidden className="h-px shrink-0" />
        </div>
      </div>
    </div>
  );
}
